import { lazy, Suspense, useEffect, useState } from "react";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { AppRoutes } from "@/constants/routes";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { useAuth } from "@/hooks/useAuth";
import { FieldValues, useForm, useWatch } from "react-hook-form";

import { RequestTypes, TipoMoneda } from "../../../../constants/enums";

import { useRoles } from "@/hooks/useRoles";
import { FutureContractAmountAndActions } from "@/components/common/section-components/requests/future-contract/FutureContractAmountAndActions";
import { FutureContractDetailMainDataCard } from "@/components/common/section-components/requests/future-contract/FutureContractMainDataCard";
import { NegotiationLastMessage } from "@/components/common/section-components/chat/NegotiationLastMessage";
import { FutureContractDetail } from "../schema/future-contract.schema";
import {
  fetchFutureContractDetailHandler,
  handleExecuteFutureContractHandler,
  handleRejectFutureContractHandler,
  sendFutureContractCommunicationHandler,
  sendFutureContractNegotiationHandler,
  updateFutureContractValuesHandler,
} from "../schema/handler-functions";

import {
  validateDeliveryMonth,
  validateTon,
} from "@/utils/request-validations";

export const FutureContractDetailPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const handleBack = () => {
    navigate(AppRoutes.GRAIN_FUTURE_CONTRACTS);
  };

  const [futureContract, setFutureContract] =
    useState<FutureContractDetail | null>();

  const fetchFutureContract = async (showLoad: boolean = true) => {
    await fetchFutureContractDetailHandler({
      fetchPermissions,
      navigate,
      setFutureContract,
      setLoadingFutureContract,
      showLoad,
      id,
    });
  };

  const [loadingFutureContract, setLoadingFutureContract] =
    useState<boolean>(false);

  useEffect(() => {
    if (isActive && id && user) fetchFutureContract();
  }, [isActive, id, user]);

  const [isEditingAmount, setIsEditingAmount] = useState<boolean>(false);
  const [loadingEditAmount, setLoadingEditAmount] = useState<boolean>(false);

  const [isEditingTon, setIsEditingTon] = useState<boolean>(false);
  const [loadingEditTon, setLoadingEditTon] = useState<boolean>(false);

  const [isEditingDeliveryMonth, setIsEditingDeliveryMonth] =
    useState<boolean>(false);
  const [loadingEditDeliveryMonth, setLoadingEditDeliveryMonth] =
    useState<boolean>(false);

  const [loadingSendNegotiation, setLoadingSendNegotiation] =
    useState<boolean>(false);
  const [loadingSendCommunication, setLoadingSendCommunication] =
    useState<boolean>(false);

  const [openRejectModal, setOpenRejectModal] = useState<boolean>(false);
  const [loadingRejectFutureContract, setLoadingRejectFutureContract] =
    useState<boolean>(false);
  const [loadingExecuteFutureContract, setLoadingExecuteFutureContract] =
    useState<boolean>(false);

  const { setOpen: setOpenExecuteModal, open: openExecuteModal } =
    useDisclosure();

  const handleExecuteFutureContract = async (event?: FieldValues) => {
    await handleExecuteFutureContractHandler({
      navigate,
      setLoadingExecuteFutureContract,
      futureContract,
      fetchPermissions,
      watchedValues: event,
    });
  };

  const handleRejectFutureContract = async (event?: FieldValues) => {
    await handleRejectFutureContractHandler({
      navigate,
      setLoadingRejectFutureContract,
      setOpenRejectModal,
      event,
      futureContract,
      fetchPermissions,
    });
  };

  const {
    control,
    formState: { errors },
    setError,
    setValue,
    clearErrors,
  } = useForm();

  const watchedValues = useWatch({
    control,
  });

  const handleEditAmount = async () => {
    await updateFutureContractValuesHandler({
      errors,
      setLoadingEdit: setLoadingEditAmount,
      futureContract,
      fetchFutureContract,
      setIsEditing: setIsEditingAmount,
      watchedValues,
      navigate,
      fetchPermissions,
      field: "amount",
    });
  };

  const handleEditTon = async () => {
    await updateFutureContractValuesHandler({
      errors,
      setLoadingEdit: setLoadingEditTon,
      futureContract,
      fetchFutureContract,
      setIsEditing: setIsEditingTon,
      watchedValues,
      navigate,
      fetchPermissions,
      field: "ton",
    });
  };

  const handleEditDeliveryMonth = async () => {
    await updateFutureContractValuesHandler({
      errors,
      setLoadingEdit: setLoadingEditDeliveryMonth,
      futureContract,
      fetchFutureContract,
      setIsEditing: setIsEditingDeliveryMonth,
      watchedValues,
      navigate,
      fetchPermissions,
      field: "delivery",
    });
  };

  const handleNoBase = () => {
    if (futureContract && !futureContract?.sinBase) {
      setValue("future_contract_amount", futureContract?.montoSolicitado);
    }
  };

  useEffect(() => {
    handleNoBase();
  }, [futureContract]);

  const { setOpen: setOpenCommunication, open: openCommunication } =
    useDisclosure();

  const { open: openChatViewModal, setOpen: setOpenChatViewModal } =
    useDisclosure();

  const { fetchPermissions } = useRoles();

  const handleSendNegotiation = async () => {
    await sendFutureContractNegotiationHandler({
      navigate,
      setLoadingSendNegotiation,
      futureContract,
      fetchFutureContract,
      fetchPermissions,
    });
  };

  const handleSendCommunication = async (event?: FieldValues) => {
    // No se pasa setOpenCommunicationModal: el chat es multi-turno y debe
    // permanecer abierto después de enviar.
    return await sendFutureContractCommunicationHandler({
      navigate,
      setLoadingSendCommunication,
      event,
      futureContract,
      fetchFutureContract,
      fetchPermissions,
    });
  };

  useEffect(() => {
    if (!futureContract?.toneladas) return;
    validateTon({
      clearErrors,
      setError,
      watchedTon: watchedValues?.future_contract_ton,
      fieldName: "future_contract_ton",
      min: 1,
    });
  }, [watchedValues?.future_contract_ton, errors]);

  useEffect(() => {
    if (!futureContract?.entrega) return;
    validateDeliveryMonth({
      clearErrors,
      setError,
      watchedDeliveryMonth: watchedValues?.future_contract_delivery_month,
      fieldName: "future_contract_delivery_month",
    });
  }, [watchedValues?.future_contract_delivery_month, errors]);

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title={"Solicitud de venta de granos a futuro"}
        onBack={handleBack}
        backButton
        loading={loadingFutureContract}
      />

      {!loadingFutureContract && (
        <>
          <FutureContractAmountAndActions
            clearErrors={clearErrors}
            futureContract={futureContract}
            control={control}
            errors={errors}
            setIsEditingAmount={setIsEditingAmount}
            setIsEditingTon={setIsEditingTon}
            setIsEditingDeliveryMonth={setIsEditingDeliveryMonth}
            handleEditAmount={handleEditAmount}
            handleEditTon={handleEditTon}
            loadingEditAmount={loadingEditAmount}
            loadingEditTon={loadingEditTon}
            loadingEditDeliveryMonth={loadingEditDeliveryMonth}
            isEditingAmount={isEditingAmount}
            isEditingTon={isEditingTon}
            isEditingDeliveryMonth={isEditingDeliveryMonth}
            handleEditDeliveryMonth={handleEditDeliveryMonth}
            loadingFutureContract={loadingFutureContract}
            setOpenRejectModal={setOpenRejectModal}
            loadingRejectFutureContract={loadingRejectFutureContract}
            loadingExecuteFutureContract={loadingExecuteFutureContract}
            handleExecuteFutureContract={() => setOpenExecuteModal(true)}
            setValue={setValue}
            loadingSendNegotiation={loadingSendNegotiation}
            loadingSendCommunication={loadingSendCommunication}
            setOpenInfoModal={setOpenCommunication}
            handleSendNegotiation={handleSendNegotiation}
          />

          <FutureContractDetailMainDataCard futureContract={futureContract} />

          <NegotiationLastMessage
            title="Último mensaje - Comunicación"
            messages={futureContract?.mensajes || []}
            onOpenChat={() => setOpenChatViewModal(true)}
            testId="future-contract-open-chat"
          />
        </>
      )}

      {futureContract?.id && (
        <Suspense>
          <RejectRequestModalDynamic
            open={openRejectModal}
            setOpen={setOpenRejectModal}
            handleAccept={handleRejectFutureContract}
            loadingAccept={loadingRejectFutureContract}
            idTipoSolicitud={RequestTypes.ContratoFuturo}
            entityName="venta de granos a futuro"
          />
          <NegotiationChatModalDynamic
            open={openCommunication}
            setOpen={setOpenCommunication}
            title="Chat - En comunicación"
            messages={futureContract?.mensajes || []}
            handleSend={handleSendCommunication}
            loadingSend={loadingSendCommunication}
            idTipoSolicitud={RequestTypes.ContratoFuturo}
            mensajesDisponibles={futureContract?.mensajesDisponibles}
          />

          <NegotiationChatModalDynamic
            open={openChatViewModal}
            setOpen={setOpenChatViewModal}
            title="Chat - En comunicación"
            messages={futureContract?.mensajes || []}
            readOnly
          />
          <ExecuteGrainRequestModalDynamic
            open={openExecuteModal}
            setOpen={setOpenExecuteModal}
            handleAccept={handleExecuteFutureContract}
            loadingAccept={loadingExecuteFutureContract}
            currencyType={futureContract?.tipoMoneda === TipoMoneda.USD ? "U$D " : undefined}
          />
        </Suspense>
      )}
    </Flex>
  );
};

const RejectRequestModalDynamic = lazy(() =>
  import("@/components/modal/RejectRequestModal").then((el) => ({
    default: el["RejectRequestModal"],
  })),
);

const NegotiationChatModalDynamic = lazy(() =>
  import("@/components/modal/NegotiationChatModal").then((el) => ({
    default: el["NegotiationChatModal"],
  })),
);

const ExecuteGrainRequestModalDynamic = lazy(() =>
  import("@/components/modal/ExecuteGrainRequestModal").then((el) => ({
    default: el["ExecuteGrainRequestModal"],
  })),
);